import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {User} from '../../../domain/user';
import {UUID} from 'angular2-uuid';
import {Session} from '../../../domain/session';
import {ConfirmationDialogComponent} from '../../../component/confirmation-dialog/confirmation-dialog.component';
import {Carrier} from '../../../domain/carrier';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {PopupDialogBase} from '../../../component/popup-dialog-base';
import {RequestExecutor} from '../../../util/request-executor';
import {MessageService} from '../../../component/message.service';
import {Router} from '@angular/router';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.css'],
  standalone: false
})
export class UserEditComponent extends PopupDialogBase implements OnInit {
  userForm: UntypedFormGroup;

  user: User;
  saving: boolean;
  deleting: boolean;

  carriers: Carrier[] = [];

  deleteConfirmDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  constructor(
    formBuilder: UntypedFormBuilder,
    public session: Session,
    private dialog: MatDialog,
    private request: RequestExecutor,
    private messageService: MessageService,
    private router: Router
  ) {
    super();

    this.carriers = session.getCarriers().filter(value => value.Active);

    this.userForm = formBuilder.group({
      Name: ['', Validators.required],
      UN: ['', Validators.required],
      Pwd: [''],
      Active: [false, Validators.required],
      Type: ['', Validators.required],
      Carriers: [[]]
    });
  }

  ngOnInit() {
    if (!this.session.userMaintenanceIsAllowed()) {
      this.userForm.disable();
    }

    if (this.user.Carriers === undefined) {
      this.user.Carriers = [];
    }

    if (this.isNewUser()) {
      this.userForm.controls.Pwd.setValidators(Validators.required);
    }

    if (!this.isNewUser()) {
      this.userForm.setValue(
        {
          Name: this.user.Name,
          UN: this.user.Username,
          Pwd: '',
          Active: this.user.Active,
          Type: this.user.Type,
          Carriers: this.user.Carriers.map(r => r.Id)
        });
    }
  }

  save() {
    Object.keys(this.userForm.controls).map((controlName) => {
      this.userForm.get(controlName).markAsTouched({onlySelf: true});
    });

    if (this.userForm.status !== 'INVALID') {
      if (this.isNewUser()) {
        this.user.Id = UUID.UUID();
        this.user.CompanyId = this.session.getCompanyId();
      }

      if (this.carriers.length === 0) {
        this.user.Carriers = [];
      } else {
        this.user.Carriers = this.userForm.value.Carriers.map(id => this.carriers.find(r => r.Id === id));
      }

      this.user.Routes = [];

      this.user.Name = this.userForm.value.Name;
      this.user.Username = this.userForm.value.UN;

      if (this.userForm.controls.Pwd.touched && this.userForm.controls.Pwd.value !== '') {
        this.user.Password = this.userForm.value.Pwd;
      } else {
        this.user.Password = undefined;
      }

      this.user.Active = this.userForm.value.Active;
      this.user.Type = this.userForm.value.Type;

      this.saving = true;

      this.request.post('/user', this.user)
        .then(() => {
          this.close();
        })
        .catch(error => {
          if (error.status == 401) {
            this.router.navigate(['login']);
          } else {
            this.messageService.showBottomErrorSnackBar(`Kunne ikke lagre brukeren (${error.status})`);
          }
        })
        .finally(() => this.saving = false);
    }
  }

  isNewUser() {
    return this.user.Id == null;
  }

  deleteUser() {
    this.deleteConfirmDialogRef = this.dialog.open(ConfirmationDialogComponent);

    this.deleteConfirmDialogRef.componentInstance.title = 'Bekreft sletting';
    this.deleteConfirmDialogRef.componentInstance.description = 'Vil du slette brukeren?';

    this.deleteConfirmDialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.request.delete('/user', {id: this.user.Id})
          .then(() => {
            this.close();
          })
          .catch(error => {
            if (error.status == 401) {
              this.router.navigate(['login']);
            } else {
              this.messageService.showBottomErrorSnackBar(`Kunne ikke slette brukeren (${error.status})`);
            }
          })
          .finally(() => this.deleting = false);
      }
    });
  }

  cancel() {
    this.close();
  }

  isNotCurrentUser() {
    return this.session.getUserId() !== this.user.Id;
  }

  hasRoutes() {
    return this.carriers !== undefined && this.carriers.length > 0;
  }
}
